import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@kollez/design-system';
import { apiGet, apiPost } from '@/lib/api';

interface AisheHit {
  _id: string;
  aisheCode?: string;
  name: string;
  city?: string;
  state?: string;
  type?: string;
  affiliatedUniversity?: string;
}

/**
 * /group/colleges/new — direct port of kollez/pages/group/college-new.html.
 *   AISHE search hits `/platform/colleges` (national master); creating hits `POST /group/colleges`.
 */
export function GroupCollegeNewPage() {
  const nav = useNavigate();
  const toast = useToast();
  const qc = useQueryClient();

  const [q, setQ] = useState('');
  const [name, setName] = useState('');
  const [aishe, setAishe] = useState('');
  const [type, setType] = useState<'Engineering' | 'Pharmacy' | 'Management' | 'Degree' | 'Polytechnic' | 'Other'>('Engineering');
  const [city, setCity] = useState('');
  const [university, setUniversity] = useState('');
  const [tpoEmail, setTpoEmail] = useState('');
  const [tpoName, setTpoName] = useState('');

  const search = useQuery({
    queryKey: ['aishe-search', q],
    queryFn: () => apiGet<{ rows: AisheHit[] }>(`/platform/colleges?q=${encodeURIComponent(q)}&limit=6`).catch(() => ({ rows: [] })),
    enabled: q.trim().length > 2,
  });

  const create = useMutation({
    mutationFn: (input: Record<string, unknown>) => apiPost('/group/colleges', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['group', 'colleges'] }),
  });

  function pickHit(h: AisheHit) {
    setName(h.name);
    setAishe(h.aisheCode ?? '');
    setCity(h.city ?? '');
    setUniversity(h.affiliatedUniversity ?? '');
    if (h.type === 'Engineering' || h.type === 'Pharmacy' || h.type === 'Management' || h.type === 'Degree' || h.type === 'Polytechnic') {
      setType(h.type);
    }
    setQ('');
  }

  function submit() {
    if (name.trim().length < 3) { toast.show({ tone: 'warning', message: 'Enter the college name.' }); return; }
    create.mutate(
      { name: name.trim(), aisheCode: aishe.trim() || undefined, type, city: city.trim() || undefined, affiliatedUniversity: university.trim() || undefined, tpoEmail: tpoEmail.trim() || undefined, tpoName: tpoName.trim() || undefined },
      {
        onSuccess: () => {
          // Deep-audit fix (Page 59/N): old toast promised 'verification
          // usually clears within 1 working day.' — no self-terminating
          // verification job exists; verification is a platform_operator
          // manual review with no bounded SLA. Rewrote honestly.
          toast.show({ message: `${name} added as provisional. A platform reviewer will verify it before it goes live.` });
          setTimeout(() => nav('/group/colleges'), 700);
        },
        onError: (e) => toast.show({ tone: 'warning', message: (e as Error)?.message ?? 'Failed to add college' }),
      },
    );
  }

  return (
    <div className="stack" style={{ paddingBottom: 96 }}>
      <div className="page-header">
        <div className="ph-text">
          <p className="eyebrow">Institutions</p>
          <h1 className="h-page">Add a college</h1>
          <p className="subhead">Search the national AISHE master. If it isn't there, add it manually — we'll verify within a day.</p>
        </div>
      </div>

      <section className="card card-pad grid gap-3">
        <label className="grid gap-1.5">
          <span className="label">Search AISHE master</span>
          <div className="input-group">
            <Search size={14} />
            <input className="input" placeholder="College name, AISHE code, or city" value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
        </label>

        {search.data && search.data.rows.length > 0 && (
          <div className="grid gap-1">
            {search.data.rows.map((h) => (
              <button
                key={h._id}
                type="button"
                className="card p-3 text-left cursor-pointer flex items-center gap-3"
                onClick={() => pickHit(h)}
              >
                <div className="flex-1 min-w-0">
                  <div className="cell-title">{h.name}</div>
                  <div className="cell-sub">{[h.aisheCode, h.city, h.state, h.affiliatedUniversity].filter(Boolean).join(' · ')}</div>
                </div>
                <span className="badge">{h.type ?? '—'}</span>
              </button>
            ))}
          </div>
        )}
      </section>

      <section className="card card-pad grid gap-4">
        <h2 className="h-section">Basics</h2>
        <div className="grid gap-4 sm:grid-cols-2">
          <label className="grid gap-1.5"><span className="label">Registered name</span><input className="input" value={name} onChange={(e) => setName(e.target.value)} /></label>
          <label className="grid gap-1.5"><span className="label">AISHE code</span><input className="input" value={aishe} onChange={(e) => setAishe(e.target.value)} /></label>
          <label className="grid gap-1.5">
            <span className="label">Type</span>
            <select className="select" value={type} onChange={(e) => setType(e.target.value as typeof type)}>
              <option>Engineering</option><option>Pharmacy</option><option>Management</option><option>Degree</option><option>Polytechnic</option><option>Other</option>
            </select>
          </label>
          <label className="grid gap-1.5"><span className="label">City</span><input className="input" value={city} onChange={(e) => setCity(e.target.value)} /></label>
          <label className="grid gap-1.5"><span className="label">Affiliating university</span><input className="input" value={university} onChange={(e) => setUniversity(e.target.value)} placeholder="e.g. JNTU Kakinada" /></label>
        </div>
      </section>

      <section className="card card-pad grid gap-4">
        <h2 className="h-section">First placement officer</h2>
        <div className="grid gap-4 sm:grid-cols-2">
          <label className="grid gap-1.5"><span className="label">Name</span><input className="input" value={tpoName} onChange={(e) => setTpoName(e.target.value)} /></label>
          <label className="grid gap-1.5"><span className="label">Work email</span><input className="input" type="email" value={tpoEmail} onChange={(e) => setTpoEmail(e.target.value)} /></label>
        </div>
        <p className="helper m-0">They'll get a sign-in link and set up their college's workspace.</p>
      </section>

      <div className="wizard-foot" style={{ borderRadius: 10 }}>
        <button className="btn btn-ghost" onClick={() => nav('/group/colleges')}>Cancel</button>
        <button className="btn btn-primary ml-auto" disabled={create.isPending} onClick={submit}>
          {create.isPending ? 'Adding…' : 'Add college'}
        </button>
      </div>
    </div>
  );
}
